"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";

const NAV_ITEMS = [
  { path: "/", label: "図書館", icon: "🏛️", iconKey: "nav_library" },
  { path: "/map", label: "地図", icon: "🕸️", iconKey: "nav_map" },
  { path: "/gacha/select", label: "ガチャ", icon: "🎲", iconKey: "nav_gacha" },
  { path: "/shelf", label: "書庫", icon: "📚", iconKey: "nav_reading" },
  { path: "/spirit", label: "精霊", icon: "✨", iconKey: "nav_spirit" },
  { path: "/settings", label: "設定", icon: "⚙️", iconKey: "nav_settings" },
] as const;

type NavPath = (typeof NAV_ITEMS)[number]["path"];

/** その画面が属するタブ。自分のタブを持たない画面（図鑑・背景・手持ち）は null */
function ownTab(pathname: string): NavPath | null {
  if (pathname === "/") return "/";
  if (pathname.startsWith("/map")) return "/map";
  if (pathname.startsWith("/gacha") || pathname.startsWith("/spirit/gacha")) return "/gacha/select";
  if (pathname.startsWith("/shelf")) return "/shelf";
  if (pathname.startsWith("/spirit")) return "/spirit";
  if (pathname.startsWith("/settings")) return "/settings";
  return null;
}

/** 自分のタブを持たない画面を直接開いたときに光らせるタブ */
function fallbackTab(pathname: string): NavPath | null {
  if (pathname.startsWith("/backgrounds") || pathname.startsWith("/party")) return "/";
  if (pathname.startsWith("/encyclopedia")) return "/spirit";
  return null;
}

export default function BottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const [uiIcons, setUiIcons] = useState<Record<string, string>>({});
  // 自分のタブを持たない画面では、来た元のタブを光らせたままにする
  const own = ownTab(pathname);
  const [lastTab, setLastTab] = useState<NavPath | null>(own);
  if (own && own !== lastTab) setLastTab(own);
  const activeTab = own ?? lastTab ?? fallbackTab(pathname);

  useEffect(() => {
    fetch("/api/ui-icons")
      .then((r) => r.ok ? r.json() : {})
      .then((data) => setUiIcons(data))
      .catch(() => {});
  }, []);

  if (pathname === "/login" || pathname.startsWith("/read/")) {
    return null;
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-violet-500/20 bg-[var(--background)]/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-lg items-center justify-around py-2">
        {NAV_ITEMS.map((item) => {
          const isActive = item.path === activeTab;
          const customIcon = uiIcons[item.iconKey];

          return (
            <button
              key={item.path}
              onClick={() => router.push(item.path)}
              className={`flex flex-col items-center gap-0.5 px-2 py-1 transition-all ${
                isActive
                  ? "text-violet-400"
                  : "text-[var(--foreground)] opacity-40 hover:opacity-60"
              }`}
            >
              {customIcon ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={customIcon} alt={item.label} className="h-6 w-6 object-contain" />
              ) : (
                <span className="text-xl">{item.icon}</span>
              )}
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
